import { InfoCircleOutlined, LeftOutlined } from "@ant-design/icons";
import {
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  notification,
  Row,
  Select,
  Spin,
} from "antd";
import { useForm } from "antd/es/form/Form";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { useGetBreakpoints } from "../../hooks/useGetBreakpoints";
import { labelComponent } from "../../util/UtilComponents";
import {
  isCommissionAgent,
  isManufacturer,
  isManufacturerCumExporter,
  showAlert,
} from "../../util/helper";
import {
  BUSINESS_TYPE,
  OFFER_PRODUCT_BUSINESS_TYPE,
} from "../../util/Constant";
import { getLabel, isValidElement, isValidString } from "../../util/ApiHelper";
import Upload from "../../components/Upload";
import {
  createOfferProductAction,
  getOfferProductsAction,
  getOfferProductsById,
} from "../../appRedux/actions/myOfferProductsAction";
import { getProductsAction } from "../../appRedux/actions";
import { useParams } from "react-router-dom";
import moment from "moment";
import { LoadingOutlined } from "@ant-design/icons";

const OfferProductDetails = () => {
  const dispatch = useDispatch();
  const [form] = useForm();
  const history = useHistory();
  const [dataId, setDataId] = useState();
  const { isMobile } = useGetBreakpoints();
  const { Option } = Select;
  const { TextArea } = Input;
  const { id } = useParams();
  const currency = Form.useWatch("currency", form);
  const productsUnitList = useSelector(
    ({ myProducts }) => myProducts.productsUnitList || []
  );
  const [visibleAttachments, setVisibleAttachments] = useState(1);
  const [fileList, setFileList] = useState([
    { label: "primary-image", src: "" },
    { src: "" },
    { src: "" },
    { src: "" },
  ]);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const authUser = useSelector(({ auth }) => auth.userData);
  const offerProductDetails = useSelector(
    (state) => state.offerProducts.productsDetailsById
  );
  const selectedUOM = Form.useWatch("uom", form);
  const paymentTerms = Form.useWatch("paymentTerms", form);
  let isEditDisabled = false;
  const uniqueProductsUnitList = Array.from(
    new Map(
      (productsUnitList || []).filter(Boolean).map((item) => [item.label, item])
    ).values()
  );
  const productList = useSelector((state) => state.myProducts.productsList);
  const existingOffers = useSelector(
    (state) => state.offerProducts.offerProductsList?.data || []
  );
  const isLoading = useSelector((state) => state.offerProducts.loading);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);

  const antIcon = <LoadingOutlined style={{ fontSize: 38 }} spin />;

  useEffect(() => {
    if (id) {
      dispatch(getOfferProductsAction());
      dispatch(getOfferProductsById(id));
    } else {
      dispatch(getOfferProductsAction());
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (isValidElement(offerProductDetails) && isValidString(id)) {
      const expiryDate = moment(offerProductDetails?.expiryDate).format(
        "DD-MM-YYYY"
      );
      form.setFieldsValue({
        productName: offerProductDetails?.name,
        expiryDate: moment(expiryDate, "DD-MM-YYYY"),
        uom: offerProductDetails?.uom,
        price: offerProductDetails?.price,
        paymentTerms: offerProductDetails?.paymentTerms,
        locationForDelivery: offerProductDetails?.locationForDelivery,
        comments: offerProductDetails?.comments,
        availableQuantity: offerProductDetails?.qty,
      });
      setDataId(id);
      let existingImages = [];

      if (typeof offerProductDetails?.primaryImage === "string") {
        existingImages = [
          { src: offerProductDetails.primaryImage, label: "primary-image" },
        ];
      } else if (Array.isArray(offerProductDetails?.primaryImage)) {
        existingImages = [...offerProductDetails.primaryImage];
      }
      const images = existingImages.slice();
      while (images.length < 4) {
        images.push({ src: "" });
      }
      setFileList(images.slice(0, 4));
      const filledImagesCount = existingImages.filter((img) => img?.src).length;
      setVisibleAttachments(Math.min(filledImagesCount + 1, 4));
    }
  }, [form, id, offerProductDetails]);

  const onFinish = async (values) => {
    try {
      if (!id) {
        const existingProduct = existingOffers.find(
          (offer) =>
            offer.name?.toLowerCase() === values.productName?.toLowerCase()
        );

        if (existingProduct) {
          const newExpiryDate = moment(values.expiryDate);
          const existingExpiryDate = moment(existingProduct.expiryDate);

          if (
            newExpiryDate.isBefore(existingExpiryDate) ||
            newExpiryDate.isSame(existingExpiryDate, "day")
          ) {
            notification.warning({
              message: "Expiry Date Conflict",
              description: `This product already exists with an expiry date of ${existingExpiryDate.format(
                "DD-MM-YYYY"
              )}. You must provide a later expiry date or can edit in the existing offer`,
            });
            return;
          }
        }
      }

      const data = {
        productId: selectedProductId,
        categoryId: selectedCategoryId,
        businessTypeIds: [OFFER_PRODUCT_BUSINESS_TYPE.exporter],
        qty: parseInt(values?.availableQuantity) || "",
        userId: authUser?.id,
        name: values?.productName,
        expiryDate: values?.expiryDate,
        uom: values?.uom,
        price: values?.price,
        paymentTerms: values?.paymentTerms,
        locationForDelivery: values?.locationForDelivery,
        comments: values?.comments,
        primaryImage: isValidString(fileList[0].src) ? fileList[0]?.src : "",
      };

      const result = await dispatch(createOfferProductAction(data, dataId));  

      if (result?.success !== false) {
        notification.success({
          message: "Success",
          description: `Offer product ${
            dataId ? "updated" : "created"
          } successfully!`,
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);

      notification.error({
        message: "Error",
        description: "Something went wrong. Please try again.",
      });
    }
  };

  const onUpload = (data) => {
    setFileList((prevFileList) => {
      const updatedList = prevFileList.map((file, index) =>
        index === data.index
          ? { ...file, src: data.src, originalName: data.originalName }
          : file
      );

      if (visibleAttachments < 4 && data.index === visibleAttachments - 1) {
        setVisibleAttachments(visibleAttachments + 1);
      }

      return updatedList;
    });
  };

  const onRemove = (data) => {
    setFileList((prevFileList) => {
      const updatedList = prevFileList.map((file, index) =>
        index === data.index ? { ...file, src: "" } : file
      );

      if (data.index === visibleAttachments - 1) {
        setVisibleAttachments(Math.max(1, visibleAttachments - 1));
      }

      return updatedList;
    });
  };
  const validateTextArea = (_, value) => {
    if (value && value.length > 1000) {
      return Promise.reject(
        new Error("Product Requirements cannot exceed 1000 characters")
      );
    }
    return Promise.resolve();
  };

  useEffect(() => {
    dispatch(getProductsAction());
  }, [dispatch]);

  return (
    <div style={{ position: "relative" }}>
      <Card
        className="myexim-detail-card"
        title={
          <div className="myexim-table-title gx-text-capitalize">
            <LeftOutlined
              className={"back-icon"}
              size="large"
              onClick={() => {
                history.goBack();
              }}
            />
            {dataId ? "Edit" : "Add"} Offer Product
          </div>
        }
      >
        {isSubmitting && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              zIndex: 1050,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              borderRadius: "8px",
            }}
          >
            <Spin indicator={antIcon} size="large" />
            <div
              style={{
                marginTop: 16,
                fontSize: 16,
                fontWeight: 500,
                color: "#1890ff",
              }}
            >
              {dataId ? "Updating" : "Creating"} offer product...
            </div>
          </div>
        )}
        <Form form={form} onFinish={onFinish} scrollToFirstError={true}>
          <Row gutter={16}>
            <Col span={8} xs={24} sm={12} md={12}>
              <Form.Item
                labelCol={{ span: 24 }}
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
                wrapperCol={{ span: 24 }}
                label={labelComponent(
                  "Product Name",
                  "Product name displayed here is based on your products added under 'My Products'"
                )}
                name="productName"
                rules={[
                  {
                    required: true,
                    message: "Product Name is required",
                  },
                ]}
              >
                <Select
                  showSearch
                  size="large"
                  placeholder="Select a Product"
                  getPopupContainer={(trigger) => trigger.parentNode}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={(value) => {
                    const selectedProduct = productList.rows.find(
                      (product) => product.name === value
                    );
                    if (selectedProduct) {
                      setSelectedProductId(selectedProduct.id);
                      setSelectedCategoryId(selectedProduct.categoryId);
                    }
                  }}
                >
                  {productList?.rows?.map((product) => (
                    <Option key={product.id} value={product.name}>
                      {product.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                disabled={isEditDisabled}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                label={labelComponent("Available Quantity", null)}
                name="availableQuantity"
                rules={[
                  {
                    required: true,
                    message: "Available Quantity",
                  },
                ]}
              >
                <Input
                  disabled={isEditDisabled}
                  placeholder="Available Quantity"
                  size={"large"}
                  type="number"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                disabled={isEditDisabled}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                label={labelComponent("Unit of Measurement (UOM)", null)}
                name="uom"
                rules={[
                  {
                    required: true,
                    message: "UOM is required",
                  },
                ]}
              >
                <Select
                  showSearch
                  size="large"
                  placeholder="Select Unit of Measurement"
                  disabled={isEditDisabled}
                  getPopupContainer={(trigger) => trigger.parentNode}
                >
                  {uniqueProductsUnitList?.map((option) => (
                    <Option key={option.label} value={option.label}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={8} xs={24} sm={12}>
              <Form.Item
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                label={labelComponent(
                  `Price ${selectedUOM ? `/ ${selectedUOM}` : ""}`,
                  "Price per unit of measurement"
                )}
                name="price"
                rules={[
                  {
                    required: true,
                    message: "Price is required",
                  },
                ]}
              >
                <InputNumber
                  min={1}
                  placeholder="Enter Price"
                  style={{ width: "100%" }}
                  addonBefore={
                    !!currency
                      ? currency
                      : isManufacturer(authUser) ||
                        isManufacturerCumExporter(authUser) ||
                        isCommissionAgent(authUser)
                      ? "INR"
                      : null
                  }
                />
              </Form.Item>
            </Col>

            {
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                  label={"Delivery Terms"}
                  name="paymentTerms"
                  rules={[
                    {
                      required: true,
                      message: "Delivery Terms is required",
                    },
                  ]}
                >
                  <Select
                    getPopupContainer={(trigger) => trigger.parentNode}
                    placeholder="Select Delivery Term"
                  >
                    <Option value={"EXW"}>EXW - Ex Works</Option>
                    <Option value={"EXM"}>EXM - Ex Mill</Option>
                    <Option value={"FOR"}>FOR</Option>
                  </Select>
                </Form.Item>
              </Col>
            }

            {(paymentTerms === "EXW" ||
              paymentTerms === "EXM" ||
              paymentTerms === "FOR") && (
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.paymentTerms !== currentValues.paymentTerms
                  }
                  disabled={isEditDisabled}
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                  label={labelComponent(
                    paymentTerms === "FOR"
                      ? "Location for delivery"
                      : "Exmill Location"
                  )}
                  name="locationForDelivery"
                  rules={[
                    {
                      validator: validateTextArea,
                    },
                    {
                      required: true,
                      message: "location for delivery is required",
                    },
                  ]}
                >
                  <TextArea
                    disabled={isEditDisabled}
                    placeholder={
                      paymentTerms === "FOR"
                        ? "Provide the location for delivery, so that suppliers can send appropriate quotations"
                        : "Enter the required Ex Works / Ex Mill location"
                    }
                    showCount
                    maxLength={250}
                  />
                </Form.Item>
              </Col>
            )}

            <Col span={8} xs={24} sm={12}>
              <Form.Item
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                label={labelComponent("Offer Expiry date")}
                name="expiryDate"
                rules={[
                  {
                    required: true,
                    message: "Expiry date is required",
                  },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD-MM-YYYY"
                  getPopupContainer={(trigger) => trigger.parentNode}
                  disabledDate={(current) =>
                    current && current < moment().startOf("day")
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                label={labelComponent("Comments")}
                name="comments"
                rules={[
                  {
                    validator: validateTextArea,
                  },
                  {
                    required: true,
                    message: "Comments is required",
                  },
                ]}
              >
                <TextArea
                  showCount
                  maxLength={200}
                  rows={10}
                  placeholder={"Enter Payment terms or other required details"}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <div className="sub-header"> Product Images</div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                }}
              >
                {fileList.slice(0, visibleAttachments).map((file, index) => {
                  return (
                    <Upload
                      key={index}
                      index={index}
                      src={file.src}
                      id={index}
                      label={
                        isValidString(file?.label)
                          ? file?.label
                          : getLabel(index)
                      }
                      originalName={file.originalName}
                      onUpload={onUpload}
                      onRemove={onRemove}
                    />
                  );
                })}
              </div>
            </Col>
            <Col>
              <div className="upload-tips gx-mt-4">
                <div className="header" style={{ display: "flex" }}>
                  <InfoCircleOutlined />
                  &nbsp; Tips{" "}
                </div>
                <div className="desc">
                  <Badge
                    status="default"
                    text="Supported file types: .jpg, .jpeg, .png"
                  />
                  <br />
                  <Badge status="default" text="File must not more than 5MB" />
                  <br />
                  <Badge
                    status="default"
                    text="Make sure you have good resolution"
                  />
                  <br />
                  <Badge
                    status="default"
                    text="Photo resolution must be more than 600x600 resolution"
                  />
                  <br />
                </div>
              </div>
            </Col>
          </Row>
          <Row className="gx-mt-4">
            <Col span={24}>
              <Form.Item
                className="gx-text-center gx-mr-3"
                style={{
                  justifyContent: isMobile ? "center" : "end",
                  display: "flex",
                  flex: 1,
                }}
              >
                <Button
                  type="secondary"
                  size="large"
                  style={{ width: 150 }}
                  onClick={() => {
                    history.goBack();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  size="large"
                  htmlType="submit"
                  style={{ width: 150 }}
                  loading={isSubmitting}
                  disabled={isSubmitting}
                >
                  Save
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
        {/* </Spin> */}
      </Card>
    </div>
  );
};

export default OfferProductDetails;
